

(window as any).__MAIN_TS_LOADED__ = true;
import { createApp } from 'vue'
import { setActivePinia, createPinia, storeToRefs } from 'pinia'
import { useCommonStore } from '@/stores/common.ts'

import App from './App.vue'
import router from './router'

import lang from '@/lib/lang/index'

(window as any).__MAIN_TS_IMPORTS_COMPLETED__ = true;
const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)

// Vue 전역 에러 핸들러
app.config.errorHandler = (err, instance, info) => {
	const error = err as Error
	const isChunkError =
		error?.message?.includes('Failed to fetch dynamically imported module') ||
		error?.message?.includes('Importing a module script failed') ||
		error?.message?.includes('error loading dynamically imported module') ||
		error?.message?.includes('fetch dynamically imported module')
	
	if (isChunkError) {
		window.location.reload()
		return
	}
	
	// 프로덕션 환경에서는 에러 트래킹 서비스로 전송
	if (import.meta.env.PROD) {
		// TODO: Sentry.captureException(err)
	}
}

setActivePinia(pinia)

const langArray: any = {
	ko: 'kr',
	en: 'en',
	ja: 'jp',
}

const browserLang = navigator.language
const _lang = langArray[browserLang.split('-')[0]] || 'en'
if (!localStorage.getItem('lang')) localStorage.setItem('lang', _lang)

app.provide('lang', lang)

const commonStore = useCommonStore()
if (localStorage.getItem('accessToken')) {
	commonStore.socketConnect()
}

// ===== 사용자 활동 감지로 자동 로그아웃 타이머 연장 =====
// 사용자가 페이지에서 활동할 때마다 자동 로그아웃 타이머를 리셋합니다

// 이벤트 리스너 관리 변수들
let isUserActivityListenerActive = false
let lastResetTime = 0
const THROTTLE_DELAY = 30000 // 30초마다 최대 1회만 리셋

const resetAutoLogoutTimer = () => {
	// 로그인 상태가 아니면 타이머 리셋하지 않음
	if (!localStorage.getItem('accessToken')) {
		return
	}
	
	// 현재 설정된 자동 로그아웃 시간을 가져와서 타이머를 다시 시작
	const currentTimeout = commonStore.getAutoLogout()
	if (currentTimeout > 0) {
		commonStore.setAutoLogout(currentTimeout)
	}
}

const handleUserActivity = () => {
	const now = Date.now()
	// 30초 이내에 이미 리셋했다면 무시 (성능 최적화)
	if (now - lastResetTime > THROTTLE_DELAY) {
		resetAutoLogoutTimer()
		lastResetTime = now
	}
}

// 사용자 활동 이벤트 리스너 등록 함수
const addUserActivityListeners = () => {
	if (isUserActivityListenerActive) return // 이미 등록되어 있으면 중복 등록 방지
	
	const userActivityEvents = [
		'click',           // 마우스 클릭
		'keydown',         // 키보드 입력
		'keyup',           // 키보드 입력 해제
		'mousemove',       // 마우스 이동
		'scroll',          // 스크롤
		'touchstart',      // 터치 시작 (모바일)
		'touchend',        // 터치 종료 (모바일)
		'focus',           // 요소 포커스
		'blur'             // 요소 포커스 해제
	]

	userActivityEvents.forEach(eventType => {
		document.addEventListener(eventType, handleUserActivity, { passive: true })
	})
	
	isUserActivityListenerActive = true
}

// 사용자 활동 이벤트 리스너 제거 함수 (로그아웃 시 호출)
const removeUserActivityListeners = () => {
	if (!isUserActivityListenerActive) return
	
	const userActivityEvents = [
		'click', 'keydown', 'keyup', 'mousemove', 'scroll',
		'touchstart', 'touchend', 'focus', 'blur'
	]

	userActivityEvents.forEach(eventType => {
		document.removeEventListener(eventType, handleUserActivity)
	})
	
	isUserActivityListenerActive = false
}

// 로그인 상태 확인 후 이벤트 리스너 등록
if (localStorage.getItem('accessToken')) {
	addUserActivityListeners()
}

// 전역에서 사용할 수 있도록 window 객체에 등록 (로그아웃 시 호출용)
;(window as any).removeUserActivityListeners = removeUserActivityListeners

// ===== 다중 탭 localStorage 동기화 =====
// 다른 탭에서 localStorage가 변경되면 현재 탭에도 반영
window.addEventListener('storage', (event) => {
	// accessToken 삭제 감지 (다른 탭에서 로그아웃)
	if (event.key === 'accessToken' && !event.newValue) {
		const { socket, autoLogoutInterval } = storeToRefs(commonStore)
		
		// Socket 연결 종료
		if (socket?.value) {
			try {
				socket.value.leaveParticipant()
				socket.value.socket?.disconnect()
			} catch (error) {
			}
		}
		
		// 자동 로그아웃 타이머 정리
		if (autoLogoutInterval.value) {
			clearInterval(autoLogoutInterval.value)
			autoLogoutInterval.value = null
		}
		
		// 사용자 활동 이벤트 리스너 제거
		removeUserActivityListeners()
		
		// 사용자 정보 초기화
		commonStore.setUserInfo({ userId: '', userName: '' }, {})
		
		// 로그인 페이지로 이동
		router.push({ name: 'SignIn' })
	}
	
	// tabList 동기화
	if (event.key && event.key.startsWith('tabList') && event.newValue) {
		try {
			const newTabList = JSON.parse(event.newValue)
			commonStore.tabList = newTabList
		} catch (error) {
		}
	}
	
	// 언어 설정 동기화
	if (event.key === 'lang' && event.newValue) {
		commonStore.setLang(event.newValue)
	}
	
	// 사용자 정보 동기화
	if (event.key === 'userId' && event.newValue) {
		commonStore.setUserInfo({ 
			userId: event.newValue, 
			userName: localStorage.getItem('userName') || '' 
		}, {})
	}
})

app.mount('#app')

// E2E 테스트용 API 노출 (app mount 후 - Pinia 활성화 이후)

if (import.meta.env.DEV || import.meta.env.MODE === 'development' || true) {

	// nextTick을 사용하여 Vue가 완전히 마운트된 후 실행
	import('vue').then(({ nextTick }) => {
		nextTick().then(() => {

			// UseHistory와 다른 composable들을 동적으로 import
			import('./views/editor/composable/useHistory.ts').then(({ UseHistory }) => {

				import('./stores/editor.ts').then(({ useEditorStore }) => {

					import('pinia').then(({ storeToRefs }) => {

						const existingAPI = (window as any).__EDITOR_TEST_API__ || {};
						const existingActions = existingAPI.actions || {};
						
						;(window as any).__EDITOR_TEST_API__ = {
							...existingAPI,
							stores: {
								editor: useEditorStore(),
								common: commonStore
							},
							
							actions: {
								...existingActions,
								async saveHistory() {
									try {
										const historyInstance = new UseHistory();
										await historyInstance.push();

										return { success: true };
									} catch (error) {

										return { success: false, error: String(error) };
									}
								},
								async undo() {
									try {
										const historyInstance = new UseHistory();
										const result = await historyInstance.undo();
										if (result === false) {

											return { success: false };
										}

										return { success: true };
									} catch (error) {

										return { success: false, error: String(error) };
									}
								},
								async redo() {
									try {
										const historyInstance = new UseHistory();
										const result = await historyInstance.redo();
										if (result === false) {

											return { success: false };
										}

										return { success: true };
									} catch (error) {

										return { success: false, error: String(error) };
									}
								}
							},
							
							state: {
								getUndoStackLength() {
									try {
										const { undoStack } = storeToRefs(useEditorStore());
										return undoStack.value.length;
									} catch (error) {

										return -1;
									}
								},
							getUndoStack() {
								try {
									const { undoStack } = storeToRefs(useEditorStore());
									return undoStack.value;
								} catch (error) {
									return [];
								}
							},								getRedoStackLength() {
									try {
										const { redoStack } = storeToRefs(useEditorStore());
										return redoStack.value.length;
									} catch (error) {

										return -1;
									}
								},
								getBlockCount() {
									const blocks = document.querySelectorAll('.editor--block[data-type="text"]');
									return blocks.length;
								},
								testPiniaAccess() {
									try {
										const { undoStack, redoStack, selector } = storeToRefs(useEditorStore());
										const result = {
											undoStackLength: undoStack.value.length,
											redoStackLength: redoStack.value.length,
											selectorId: selector.value?.id || 'null',
											success: true
										};

										return result;
									} catch (error) {

										return { success: false, error: String(error) };
									}
								}
							},
							
							utils: {
								focusBlock: (blockId: string) => {
									const block = document.getElementById(blockId);
									if (block) {
										block.focus();
										const editorStore = useEditorStore();
										editorStore.selector = block as HTMLElement;
									}
								},
								
								async createImageBlock(base64Data: string) {
									try {
										const { useImage } = await import('./views/editor/composable/image/useImage.ts');
										const imageDimension = { width: 400, height: 300 };
										await useImage().setImageBlock(base64Data, imageDimension);

										return { success: true };
									} catch (error) {

										return { success: false, error: String(error) };
									}
								},
								
								async createTableBlock(tableData: any) {
									try {
										const { useCreateTable } = await import('./views/editor/composable/table/useCreateTable.ts');
										const editorStore = useEditorStore();
										editorStore.tableBlockTemp = { content: tableData };
										await useCreateTable().table();

										return { success: true };
									} catch (error) {

										return { success: false, error: String(error) };
									}
								},
								
								async createFileBlock(files: Array<{originName: string, uuidFileName: string}>) {
									try {
										const { createBlockEvent } = await import('./views/editor/composable/useBlockCreate.ts');
										const { addFileBlock } = await import('./views/editor/composable/useFile.ts');
										const { UseHistory } = await import('./views/editor/composable/useHistory.ts');
										const { nextTick } = await import('vue');
										
										await createBlockEvent('file', '');
										await nextTick();
										
										const editorStore = useEditorStore();
										const currentBlock = editorStore.selector;
										if (currentBlock) {
											currentBlock.dataset.type = 'file';
											currentBlock.setAttribute('contenteditable', 'false');
											const fileContainer = addFileBlock(files);
											currentBlock.innerHTML = '';
											currentBlock.appendChild(fileContainer);
											
											const history = new UseHistory();
											await history.push();
										}

										return { success: true };
									} catch (error) {

										return { success: false, error: String(error) };
									}
								}
							}
						};
						
					}).catch((error) => {

					});
				}).catch((error) => {

				});
			}).catch((error) => {

			});
		});
	});
}
